import { commerceOfferProperties } from "@/lib/analytics-ga4";
import { trackEvent } from "@/lib/analytics-events";
import type { CheckoutChannel } from "@/lib/checkout-log-domain";
import type { CommerceSystem } from "@/lib/commerce-routing";

export function trackReportCheckoutStarted(props: {
  reading_id?: string;
  presentation_id?: string;
  order_id?: string;
  checkout_event_id?: string;
  language?: string;
  plan_id: string;
  price_inr: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source?: string;
  placement?: string;
  checkout_channel?: string;
  commerce_system?: string;
  intent?: string;
  source_path?: string;
  landing_path?: string;
  customer_problem?: string;
  urgency?: string;
  confidence?: number;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  landing_intent?: string;
  origin_placement?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
  recovery_channel?: string;
  recovery_order_id?: string;
  journey_action_id?: string;
  journey_slot?: string;
  journey_stage?: string;
  journey_action_kind?: string;
  journey_policy_id?: string;
  journey_policy_version?: number;
}) {
  trackEvent("report_checkout_started", props);
  trackEvent(
    "commerce_checkout_started",
    commerceOfferProperties({ ...props, offer_type: "report" })
  );
}

export function trackReportCheckoutAbandoned(props: {
  reading_id?: string;
  plan_id: string;
  price_inr: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  landing_path?: string;
  customer_problem?: string;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  journey_action_id?: string;
  journey_slot?: string;
  journey_stage?: string;
  journey_action_kind?: string;
  journey_policy_id?: string;
  journey_policy_version?: number;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
  recovery_channel?: string;
  recovery_order_id?: string;
}) {
  trackEvent("report_checkout_abandoned", props);
}

export function trackReportCheckoutRecovered(props: {
  reading_id?: string;
  plan_id: string;
  price_inr: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  landing_path?: string;
  customer_problem?: string;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
  recovery_channel?: string;
}) {
  trackEvent("report_checkout_recovered", props);
}

export function trackReportCheckoutRecoveryClicked(props: {
  reading_id?: string;
  plan_id?: string;
  plan_type?: string;
  source?: string;
  placement?: string;
  intent?: string;
  source_path?: string;
  recovery_channel?: string;
  recovery_order_id?: string;
}) {
  trackEvent("report_checkout_recovery_clicked", props);
}

/**
 * Fires once when the inline checkout CONTACT form (email + phone) becomes
 * visible — i.e. BEFORE Razorpay is opened. This is the only signal for the
 * pre-payment abandonment cohort: this event fires only after at least 50% of
 * the real form is visible for one second; `report_checkout_started` fires only
 * after a successful order create. The gap is therefore genuine visible-form
 * abandonment, not below-the-fold component mounts. No PII — prefill flags are
 * booleans only.
 */
export function trackReportCheckoutFormShown(props: {
  plan_id: string;
  price_inr?: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source_path?: string;
  landing_path?: string;
  landing_intent?: string;
  intent?: string;
  placement?: string;
  presentation_id?: string;
  reading_id?: string;
  prefill_email?: boolean;
  prefill_phone?: boolean;
}) {
  trackEvent("report_checkout_form_shown", props);
}

/**
 * Fires ONCE per paywall presentation when the pay-worth-value reassurance strip
 * (secure-payment / instant-access) is shown below the primary CTA. This is the
 * exposure signal for the payment-moment value-framing: it lets us measure the
 * modal-open -> capture rate with vs without the strip on the FREE lake (no new
 * mart needed -- rule 53), by joining this event to the existing
 * `razorpay_checkout_opened` -> purchase funnel. `variant` is "value_framing_v1"
 * so a future A/B can split it without a schema change.
 */
export function trackPaywallValueFramingShown(props: {
  plan_id?: string;
  plan_type?: string;
  price_inr?: number;
  source?: string;
  placement?: string;
  source_path?: string;
  variant?: string;
}) {
  trackEvent("paywall_value_framing_shown", props);
}

/**
 * Fires on the visible report CTA submit before contact/report-input/order gates.
 * This is diagnostic only: `report_checkout_started` remains the canonical
 * successful order-created checkout start.
 */
export function trackReportOfferCtaTapped(props: {
  plan_id: string;
  price_inr?: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source?: string;
  source_path?: string;
  landing_path?: string;
  landing_intent?: string;
  intent?: string;
  placement?: string;
  checkout_channel?: string;
  commerce_system?: string;
  customer_problem?: string;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  journey_action_id?: string;
  journey_slot?: string;
  journey_stage?: string;
  journey_action_kind?: string;
  journey_policy_id?: string;
  journey_policy_version?: number;
  presentation_id?: string;
  reading_id?: string;
  prefill_email?: boolean;
  prefill_phone?: boolean;
}) {
  trackEvent("report_offer_cta_tapped", props);
}

/** Diagnostic truth for CTA taps that never reach a Razorpay-open attempt. */
export function trackReportCheckoutBlocked(props: {
  plan_id: string;
  price_inr?: number;
  stage:
    "contact_gate" | "report_inputs" | "order_create" | "price_contract" | "sdk_load" | "sdk_open";
  value?: number;
  currency?: string;
  amount_minor?: number;
  reason:
    | "missing_contact"
    | "incomplete_report_inputs"
    | "order_create_failed"
    | "price_mismatch"
    | "sdk_load_failed"
    | "sdk_unavailable"
    | "sdk_open_failed";
  source?: string;
  source_path?: string;
  landing_path?: string;
  landing_intent?: string;
  intent?: string;
  placement?: string;
  checkout_channel?: string;
  commerce_system?: string;
}) {
  trackEvent("report_checkout_blocked", props);
}

/**
 * Fires once per presentation when the Brihat trade-up (AOV lever #2) is shown
 * alongside a base report CTA — the impression denominator for the upsell's
 * take-rate. Emit via useImpressionOnce (rule 47: stable string key, never an
 * object-keyed effect).
 */
export function trackCommerceTradeupShown(props: {
  base_plan_id: string;
  upgrade_plan_id: string;
  upgrade_delta_inr: number;
  source?: string;
  source_path?: string;
  intent?: string;
  placement?: string;
}) {
  trackEvent("commerce_tradeup_shown", props);
}

/** Fires when the seeker selects the Brihat upgrade option (the trade-up take). */
export function trackCommerceTradeupSelected(props: {
  base_plan_id: string;
  upgrade_plan_id: string;
  upgrade_delta_inr: number;
  source?: string;
  source_path?: string;
  intent?: string;
  placement?: string;
}) {
  trackEvent("commerce_tradeup_selected", props);
}

/**
 * Fires when Razorpay reports a UPI-specific payment timeout — the dominant
 * failure class (~94% of failed payments). Distinct from the generic
 * `razorpay_checkout_closed` so UPI-timeout volume + recovery can be measured
 * on its own, and so the card-fallback nudge shown to the user is attributable.
 */
export function trackUpiPaymentTimeout(props: {
  plan_id: string;
  plan_type?: string;
  order_id?: string;
  price_inr?: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source_path?: string;
  recovery_channel?: string;
  recovery_order_id?: string;
  high_aov_report?: boolean;
  error_reason?: string;
  error_code?: string;
}) {
  trackEvent("report_upi_payment_timeout", props);
}

/** Why the report checkout is waiting on a late provider confirmation. */
export type ReportPaymentPendingTrigger = "upi_timeout" | "upi_app_handoff" | "dismissed";

type ReportPaymentConfirmationProps = {
  plan_id: string;
  order_id: string;
  price_inr?: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source_path?: string;
  recovery_channel?: string;
  pending_trigger: ReportPaymentPendingTrigger;
};

/** The buyer left Razorpay after a UPI timeout or a UPI-app handoff, and the
 *  report CTA is now polling /payment/status for a late approval. */
export function trackReportUpiPaymentPending(props: ReportPaymentConfirmationProps) {
  trackEvent("report_upi_payment_pending", props);
}

/** /payment/status reported the order activated after Razorpay closed without
 *  a success callback (late UPI approval or webhook-only fulfilment). */
export function trackReportPaymentLateSuccess(
  props: ReportPaymentConfirmationProps & { poll_attempt: number; elapsed_ms: number }
) {
  trackEvent("report_payment_late_success", props);
}

/** One-tap retry from a pending or failed report checkout. `same_order` is
 *  false only when the saved order can no longer take a payment. */
export function trackReportPaymentRetryClicked(props: {
  plan_id: string;
  order_id?: string;
  price_inr?: number;
  source_path?: string;
  recovery_channel?: string;
  retry_method: "upi" | "card";
  payment_phase: "confirming" | "abandoned";
  same_order: boolean;
}) {
  trackEvent("report_payment_retry_clicked", props);
}

export function trackChatUpiPaymentTimeout(props: {
  order_id?: string;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source_path?: string;
  recovery_channel?: string;
  recovery_order_id?: string;
  error_reason?: string;
  error_code?: string;
}) {
  trackEvent("chat_upi_payment_timeout", props);
}

export function trackReportPurchased(props: {
  reading_id?: string;
  plan_id: string;
  price_inr: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  source?: string;
  placement?: string;
  order_id: string;
  checkout_channel?: string;
  commerce_system?: string;
  intent?: string;
  source_path?: string;
  landing_path?: string;
  customer_problem?: string;
  urgency?: string;
  confidence?: number;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
  recovery_channel?: string;
  recovery_order_id?: string;
}) {
  trackEvent("report_purchased", props);
}

export function trackRevenueCompleted(props: {
  reading_id?: string;
  revenue_inr?: number;
  value?: number;
  currency?: string;
  amount_minor?: number;
  order_id: string;
  commerce_system: CommerceSystem;
  checkout_channel: CheckoutChannel;
  source?: string;
  source_path?: string;
  landing_path?: string;
  intent?: string;
  customer_problem?: string;
  urgency?: string;
  confidence?: number;
  placement?: string;
  offer_type?: string;
  plan_id?: string;
  product_sku?: string;
  product_name?: string;
  route_rank_score?: number;
  route_rank_reason?: string;
  ranked_offer_order?: string[];
  agent_trace_id?: string;
  landing_intent?: string;
  detected_intent?: string;
  intent_confidence?: string;
  is_primary_cta?: boolean;
  cta_rank?: number;
  paywall_variant?: string;
  recovery_channel?: string;
  recovery_order_id?: string;
}) {
  trackEvent("revenue_completed", props);
  trackEvent(
    "commerce_purchase_completed",
    commerceOfferProperties({ ...props, offer_type: props.offer_type ?? "report" })
  );
}
